import APIConfig from 'config/api';
import {
  AuditData,
  FetchParamsModel,
  FetchResponseModel,
} from 'typings/common';
import { APIService } from '../axiosInstance';
import { IDocumentForm } from 'typings/onboarding/onboarding';
import {
  IAdditionalField,
  RelationshipTemplate,
} from 'typings/application/relationship-template';
import {
  AccountUsePercentageOptions,
  AdditionalFieldTypes,
  AmountOptions,
  CustomerOptions,
  EntityAgeOptions,
  HearAboutTenetVariants,
  NumberOfEmployeesOptions,
  NumberOfOfficesOptions,
  NumberOfTransactionsOptions,
  OrganizationAddressTypes,
  PurposeOfBankAccountOptions,
  TimeframeOptions,
  SourceOfRevenueOptions,
  TotalTransactionsPerMonthOptions,
  CounterPartyOptions,
  SubIndustryOptions,
  VirtualAssetInvolvementOptions,
} from 'enums/onboarding/crm';

export type IntakeFormStepStatus =
  | 'incomplete'
  | 'complete'
  | 'in_progress'
  | 'invalid';

export type IntakeFormStatus =
  | 'draft'
  | 'submitted'
  | 'withdrawn'
  | 'under-review'
  | 'returned'
  | 'declined'
  | 'approved'
  | 'archived'
  | 'ready-for-nbc'
  | 'pending-approval'
  | 'support';

export type IntakeFormTypes = 'onboarding-organization';

export type AdditionalFieldAnswerFromQuery = {
  id: string | null;
  type: AdditionalFieldTypes;
  name: string;
  valueJSON: string;
};

export type ApplicantRelationshipFromQuery = {
  id?: string | null;
  relationshipTemplateId?: string;
  additionalFields: AdditionalFieldAnswerFromQuery[];
};

export type AddressFromQuery = {
  id?: string | null;
  type: OrganizationAddressTypes[];
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type ServiceProviderFieldFromQuery = {
  id?: string | null;
  serviceProviderId?: string | null;
  additionalFields: AdditionalFieldAnswerFromQuery[];
};

export type FieldIssue = {
  id: string;
  fieldId: string;
  comment: string;
  resolvedWithComment: string;
  isResolved: boolean;
  resolvedAt: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

export type FieldIssueWithTemplate = FieldIssue & {
  fieldTemplate: FieldTemplate;
};

export type FieldTemplate = {
  template: string;
  values: string[];
};

export type AdditionalQuestion = {
  _id: string;
  answer: {
    isProvided: boolean;
    reason?: string;
    valueJSON?: string;
  } | null;
  field: {
    name: string;
    description: string;
    type: AdditionalFieldTypes;
    options: {
      onboardingRequired: boolean;
      selectOptions: string[];
      formDocument: IDocumentForm | null;
      documentRequiresCertification: boolean;
    };
  };
};

export type SupplementaryQuestionsFormFromQuery = {
  _id: string;
  status: IntakeFormStepStatus;
  name: string;
  description: string;
  additionalFields: IAdditionalField[];
};

export type IntakeFormFromQuery = {
  _id: string;
  hrid: string;
  state: IntakeFormStatus;
  type: IntakeFormTypes;
  completionStatuses: {
    account: IntakeFormStepStatus;
    funding: IntakeFormStepStatus;
    associatedIndividuals: IntakeFormStepStatus;
    basicInfo: IntakeFormStepStatus;
    customerAndProducts: IntakeFormStepStatus;
    entityProfile: IntakeFormStepStatus;
    entityType: IntakeFormStepStatus;
    serviceProviders: IntakeFormStepStatus;
    additionalQuestions: IntakeFormStepStatus;
    supplementaryQuestions: IntakeFormStepStatus;
  };
  fields: {
    entityCountry?: string | null;
    entityTypeCategoryId?: string | null;
    entityLegalName?: string | null;
    entityDBA?: string | null;
    hearTenet?: HearAboutTenetVariants | null;
    referrerName?: string | null;
    referrerCompany?: string | null;
    referrerMore?: string | null;
    applicantRelations?: ApplicantRelationshipFromQuery[] | null;

    entityDateFormation?: string | null;
    entityOperationStart?: EntityAgeOptions | null;
    registrationNumber?: string | null;
    isRegulated?: boolean | null;
    regulationCountry?: string[] | null;
    regulationExemption?: string | null;
    hasBearerShares?: boolean | null;
    numEmployees?: NumberOfEmployeesOptions | null;
    numOffices?: NumberOfOfficesOptions | null;
    // TODO: verify name
    isOperatingInSanctionedCountries?: boolean | null;

    entityWebsite?: string | null;
    addresses?: AddressFromQuery[] | null;

    industryCodes?: string[] | null;
    subIndustries?: SubIndustryOptions[] | null;
    isBlockchainTechnologyUsed?: boolean | null;
    isVirtualAssetsInvolvedInBusiness?: boolean | null;
    involvedVirtualAssets?: VirtualAssetInvolvementOptions[] | null;

    customerGroups?: CustomerOptions[] | null;
    customerGroupOtherDetails?: string | null;
    sourcesOfRevenue?: SourceOfRevenueOptions[] | null;
    sourceOfRevenueOtherDetails?: string | null;
    businessDescription?: string | null;
    supportingDocuments?: string[] | null;
    projectedRevenue6m?: AmountOptions | null;
    projectedRevenue6mAmount?: number | null;
    projectedRevenue12m?: AmountOptions | null;
    projectedRevenue12mAmount?: number | null;
    projectedRevenue24m?: AmountOptions | null;
    projectedRevenue24mAmount?: number | null;

    initialDeposit?: AmountOptions | null;
    initialDepositAmount?: number | null;
    sourceOfFunds?: SourceOfRevenueOptions[] | null;
    sourceOfFundsOtherDetails?: string | null;
    initialDepositCountries?: string[] | null;
    intendToTransferFundsFrame?: TimeframeOptions | null;
    intendToTransferFundsFrameDetails?: string | null;
    hasOtherBanks?: boolean | null;
    otherBanks?: string | null;
    otherBankFeedback?: string | null;
    isPrimaryAccount?: boolean | null;
    counterpartyType?: CounterPartyOptions | null;
    thirdPartyName?: string | null;
    thirdPartyRelationship?: string | null;
    plansToOpenExternalBankAccounts?: boolean | null;
    plansToOpenExternalBankAccountsDetails?: string | null;
    accountUsePercentage?: AccountUsePercentageOptions | null;

    purposeOfAccount?: PurposeOfBankAccountOptions[] | null;
    accountCountries?: string[] | null;
    incomingTransactionNumber?: NumberOfTransactionsOptions | null;
    incomingTransactionVolume?: TotalTransactionsPerMonthOptions | null;
    outgoingTransactionNumber?: NumberOfTransactionsOptions | null;
    outgoingTransactionVolume?: TotalTransactionsPerMonthOptions | null;
    avarageMonthlyBalance?: AmountOptions | null;
    accountInflowsDetails?: string | null;
    accountOutflowsDetails?: string | null;

    hasOtherBeneficialOwners?: boolean | null;
    beneficialOwners?:
      | {
          id?: string | null;
          fullName?: string | null;
          countryOfResidence?: string | null;
          relationshipToEntity?: string | null;
        }[]
      | null;

    serviceProviders?: ServiceProviderFieldFromQuery[] | null;

    supplementaryQuestions?: Record<string, AdditionalFieldAnswerFromQuery[]>;
  };
  fieldIssues: Record<
    string,
    {
      active: FieldIssue | null;
      fieldTemplate: FieldTemplate | null;
      resolved: FieldIssue[] | null;
    }
  >;
  additionalQuestions: AdditionalQuestion[];
  triggeredSupplementaryQuestionForms: SupplementaryQuestionsFormFromQuery[];
  metadata: {
    countries: Record<string, string>;
    keyRelationshipTemplateInstance: {
      id: string;
      name: string;
      description: string;
    };
    relationshipTemplates: RelationshipTemplate[];
    requiredServiceProviderIds: string[];
    createdApplicationId: string;
    serviceProviders: Record<string, ServiceProviderFromQuery>;
    documents: Record<
      string,
      {
        id: string;
        createdAt: string;
        creatorName: string;
        creatorId: string;
        files: {
          id: string;
          name: string;
          size: number;
        }[];
      }
    >;
  };
  review?: {
    summary?: string;
  };
  _audit: AuditData;
};

export type CountryFromQuery = {
  id: string;
  name: string;
  alpha2Code: string;
};

export type IntakeFormFields = IntakeFormFromQuery['fields'];

export type AdditionalQuestionAnswer = {
  questionId: string;
  answer: {
    isProvided: boolean;
    reason?: string;
    valueJSON?: string;
  } | null;
};

export type FieldIssueWithComment = { fieldId: string; comment: string };

export type UpdateIntakeFormFieldsBody = {
  fields?: IntakeFormFields;
  additionalQuestionAnswers?: AdditionalQuestionAnswer[];
  fieldIssuesToResolve?: FieldIssueWithComment[];
};

export interface FetchServiceProvidersParams extends FetchParamsModel {
  search?: string;
}

export type ServiceProviderFromQuery = {
  _id: string;
  name: string;
  description: string;
  additionalFields: IAdditionalField[];
};

export type IntakeSubmissionFromQuery = {
  applicantContactId: string;
  applicantName: string;
  assignedTo: string;
  directClientName: string;
  hrid: string;
  isActive: boolean;
  keyRelationshipTemplateName: string;
  state: IntakeFormStatus;
  submittedDate: string;
  type: IntakeFormTypes;
  _audit: AuditData;
  _id: string;
};

const intakeFormAPI = {
  fetchIntakeFormById: (intakeFormId: string) => {
    return APIService.get<{ clientIntakeSubmission: IntakeFormFromQuery }>(
      `${APIConfig.crmApi}/client-intake-submission/${intakeFormId}`,
    ).then(({ data }) => data);
  },

  fetchIntakeSubmissions: (params: FetchParamsModel) => {
    return APIService.get<FetchResponseModel<IntakeSubmissionFromQuery>>(
      `${APIConfig.crmApi}/client-intake-submission`,
      {
        params,
      },
    ).then(({ data }) => data);
  },

  fetchServiceProviders: (params: FetchServiceProvidersParams) => {
    return APIService.get<FetchResponseModel<ServiceProviderFromQuery>>(
      `${APIConfig.crmApi}/service-provider`,
      {
        params,
      },
    ).then(({ data }) => data);
  },

  setIntakeFormFields: (
    intakeFormId: string,
    data: UpdateIntakeFormFieldsBody,
  ) => {
    return APIService.post<IntakeFormFromQuery>(
      `${APIConfig.crmApi}/client-intake-submission/${intakeFormId}/set-fields`,
      data,
    ).then(({ data }) => data);
  },

  submitIntakeFormForReview: (intakeFormId: string) => {
    return APIService.post<IntakeFormFromQuery>(
      `${APIConfig.crmApi}/client-intake-submission/${intakeFormId}/submit`,
    ).then(({ data }) => data);
  },

  withdrawApplication: (intakeFormId: string) => {
    return APIService.post<IntakeFormFromQuery>(
      `${APIConfig.crmApi}/client-intake-submission/${intakeFormId}/withdraw`,
    ).then(({ data }) => data);
  },

  deactivateApplication: (intakeFormId: string) => {
    return APIService.post<IntakeFormFromQuery>(
      `${APIConfig.crmApi}/client-intake-submission/${intakeFormId}/deactivate`,
    ).then(({ data }) => data);
  },
};

export { intakeFormAPI };
